import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Role } from '@/models/AccountEnums';

// Structure: USER_ID -> ROLE_TYPE -> filters -> excluded
// groups: groupId (e.g. "investor:123") -> excluded tag names (legacy ExcludedGroups shape)
export interface ExcludedFilters {
  accounts: number[];
  categories: number[];
  accountTypes: number[];
  institutions: number[];
  tags: number[];
  groups: Record<string, string[]>;
}

interface RoleFilters {
  filters: {
    excluded: ExcludedFilters;
    // Advisor-only: which client the client-specific exclusions (accounts, tags,
    // groups) were chosen for. The key of this slice is userId+roleType, with no
    // client in it, so without this marker an advisor's account exclusions
    // follow them onto the next client they open.
    clientScope?: number;
    hideTransfers?: boolean;
  };
}

interface UserFilters {
  [roleType: string]: RoleFilters | undefined;
}

interface FilterState {
  [userId: string]: UserFilters | undefined;
}

const initialState: FilterState = {};

// Load from localStorage on initialization
const loadFromLocalStorage = (): FilterState => {
  try {
    const serializedState = localStorage.getItem('filterState');
    if (serializedState === null) {
      return initialState;
    }
    return JSON.parse(serializedState) as FilterState;
  } catch (error) {
    console.error('Error loading filter state from localStorage:', error);
    return initialState;
  }
};

// Save to localStorage
const saveToLocalStorage = (state: FilterState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('filterState', serializedState);
  } catch (error) {
    console.error('Error saving filter state to localStorage:', error);
  }
};

const createEmptyExcluded = (): ExcludedFilters => ({
  accounts: [],
  categories: [],
  accountTypes: [],
  institutions: [],
  tags: [],
  groups: {},
});

const ensureRoleFilters = (
  state: FilterState,
  userId: number,
  roleType: Role,
): RoleFilters => {
  const userIdStr = String(userId);
  let userFilters = state[userIdStr];
  if (!userFilters) {
    userFilters = {};
    state[userIdStr] = userFilters;
  }

  let roleFilters = userFilters[roleType];
  if (!roleFilters) {
    roleFilters = {
      filters: {
        excluded: createEmptyExcluded(),
      },
    };
    userFilters[roleType] = roleFilters;
  }

  return roleFilters;
};

const filterSlice = createSlice({
  name: 'filters',
  initialState: loadFromLocalStorage(),
  reducers: {
    setExcludedAccounts: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        accounts: number[];
      }>,
    ) => {
      const { userId, roleType, accounts } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.accounts = accounts;
      saveToLocalStorage(state);
    },
    setExcludedCategories: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        categories: number[];
      }>,
    ) => {
      const { userId, roleType, categories } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.categories = categories;
      saveToLocalStorage(state);
    },
    setExcludedAccountTypes: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        accountTypes: number[];
      }>,
    ) => {
      const { userId, roleType, accountTypes } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.accountTypes = accountTypes;
      saveToLocalStorage(state);
    },
    setExcludedInstitutions: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        institutions: number[];
      }>,
    ) => {
      const { userId, roleType, institutions } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.institutions = institutions;
      saveToLocalStorage(state);
    },
    setExcludedTags: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        tags: number[];
      }>,
    ) => {
      const { userId, roleType, tags } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.tags = tags;
      saveToLocalStorage(state);
    },
    setExcludedGroups: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        groups: Record<string, string[]>;
      }>,
    ) => {
      const { userId, roleType, groups } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded.groups = groups;
      saveToLocalStorage(state);
    },
    setExcludedFilters: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        excluded: ExcludedFilters;
      }>,
    ) => {
      const { userId, roleType, excluded } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.excluded = {
        accounts: excluded.accounts,
        categories: excluded.categories,
        accountTypes: excluded.accountTypes,
        institutions: excluded.institutions,
        tags: excluded.tags,
        groups: excluded.groups,
      };
      saveToLocalStorage(state);
    },
    setFilterClientScope: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        clientId: number | undefined;
      }>,
    ) => {
      const { userId, roleType, clientId } = action.payload;
      const roleFilters = ensureRoleFilters(state, userId, roleType);

      roleFilters.filters.clientScope = clientId;
      saveToLocalStorage(state);
    },
    setHideTransfers: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
        hideTransfers: boolean;
      }>,
    ) => {
      const { userId, roleType, hideTransfers } = action.payload;
      const userKey = String(userId);

      if (!(userKey in state)) {
        state[userKey] = {};
      }
      const userFilters = state[userKey];
      if (!(roleType in userFilters)) {
        userFilters[roleType] = {
          filters: {
            excluded: {
              accounts: [],
              categories: [],
              accountTypes: [],
              institutions: [],
              tags: [],
              groups: {},
            },
          },
        };
      }

      userFilters[roleType].filters.hideTransfers = hideTransfers;
      saveToLocalStorage(state);
    },
    clearExcludedFilters: (
      state,
      action: PayloadAction<{
        userId: number;
        roleType: Role;
      }>,
    ) => {
      const { userId, roleType } = action.payload;
      const roleFilters = state[String(userId)]?.[roleType];

      if (roleFilters) {
        roleFilters.filters.excluded = createEmptyExcluded();
        saveToLocalStorage(state);
      }
    },
  },
});

export const {
  setExcludedAccounts,
  setExcludedCategories,
  setExcludedAccountTypes,
  setExcludedInstitutions,
  setExcludedTags,
  setExcludedGroups,
  setExcludedFilters,
  setFilterClientScope,
  setHideTransfers,
  clearExcludedFilters,
} = filterSlice.actions;

export default filterSlice.reducer;

// Stable empty references for selectors to avoid unnecessary rerenders (Redux memoization)
const EMPTY_NUMBER_ARRAY: number[] = [];
const EMPTY_GROUPS: Record<string, string[]> = {};

const defaultExcluded: ExcludedFilters = {
  accounts: EMPTY_NUMBER_ARRAY,
  categories: EMPTY_NUMBER_ARRAY,
  accountTypes: EMPTY_NUMBER_ARRAY,
  institutions: EMPTY_NUMBER_ARRAY,
  tags: EMPTY_NUMBER_ARRAY,
  groups: EMPTY_GROUPS,
};

const asNumberList = (value: unknown, fallback: number[]): number[] =>
  Array.isArray(value) ? (value as number[]) : fallback;

const asGroupMap = (value: unknown): Record<string, string[]> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, string[]>)
    : EMPTY_GROUPS;

export const selectExcludedFilters = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): ExcludedFilters | null => {
  if (!userId || !roleType) {
    return null;
  }
  const userIdStr = String(userId);
  const roleTypeStr = roleType;
  const raw = state.filters[userIdStr]?.[roleTypeStr]?.filters.excluded as
    | {
        accounts?: unknown;
        categories?: unknown;
        accountTypes?: unknown;
        institutions?: unknown;
        tags?: unknown;
        groups?: unknown;
      }
    | undefined;
  if (!raw) return null;
  return {
    accounts: asNumberList(raw.accounts, defaultExcluded.accounts),
    categories: asNumberList(raw.categories, defaultExcluded.categories),
    accountTypes: asNumberList(raw.accountTypes, defaultExcluded.accountTypes),
    institutions: asNumberList(raw.institutions, defaultExcluded.institutions),
    tags: asNumberList(raw.tags, defaultExcluded.tags),
    groups: asGroupMap(raw.groups),
  };
};

export const selectExcludedAccounts = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number[] => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.accounts ?? EMPTY_NUMBER_ARRAY;
};

export const selectExcludedCategories = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number[] => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.categories ?? EMPTY_NUMBER_ARRAY;
};

export const selectExcludedAccountTypes = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number[] => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.accountTypes ?? EMPTY_NUMBER_ARRAY;
};

export const selectExcludedInstitutions = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number[] => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.institutions ?? EMPTY_NUMBER_ARRAY;
};

export const selectExcludedTags = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number[] => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.tags ?? EMPTY_NUMBER_ARRAY;
};

/**
 * The client the stored client-specific exclusions belong to, or `undefined` when
 * none was recorded (every investor entry, and any advisor entry persisted
 * before this field existed).
 */
export const selectFilterClientScope = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): number | undefined => {
  if (!userId || !roleType) return undefined;
  const raw = state.filters[String(userId)]?.[roleType]?.filters.clientScope;
  return typeof raw === 'number' ? raw : undefined;
};

export const selectHideTransfers = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): boolean | undefined => {
  if (!userId || !roleType) return undefined;
  const userFilters = state.filters[String(userId)];
  const roleFilters = userFilters?.[roleType];
  return roleFilters?.filters.hideTransfers;
};

export const selectExcludedGroups = (
  state: { filters: FilterState },
  userId: number | undefined,
  roleType: Role | undefined,
): Record<string, string[]> => {
  const excluded = selectExcludedFilters(state, userId, roleType);
  return excluded?.groups ?? EMPTY_GROUPS;
};
